import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { fData } from 'src/utils/format-number';
import axiosInstance, { endpoints } from 'src/utils/axios';

import { toast } from 'src/components/snackbar';
import { Markdown } from 'src/components/markdown';
import { FileThumbnail } from 'src/components/file-thumbnail';

import { useMockedUser } from 'src/auth/hooks';

import { useMessage } from '../hooks/use-message';
import { ChatMessageFeedback } from './chat-message-feedback';

// ----------------------------------------------------------------------
/** @type {any} */
const FileThumbnailAny = FileThumbnail;

// ----------------------------------------------------------------------

export function ChatMessageItem({ message, participants, onOpenLightbox, onFeedback }) {
  const { user } = useMockedUser();

  const { me, hasImage } = useMessage({
    message,
    participants,
    currentUserId: `${user?.id}`,
  });

  const { body } = message;
  const isLoadingMessage = message?.id === '__loading__';

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await axiosInstance.get(
        endpoints.knowledge.attachmentDownload(message.id, attachment.s3Key),
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], {
        type: attachment.contentType || 'application/octet-stream',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download attachment');
    }
  };

  const renderThinking = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 2,
        bgcolor: (theme) => theme.vars.palette.action.hover,
        border: (theme) => `1px solid ${theme.vars.palette.divider}`,
        boxShadow: (theme) => theme.customShadows.z1,
      }}
    >
      <Stack direction="row" spacing={0.7} alignItems="center">
        {[0, 1, 2].map((idx) => (
          <Box
            key={idx}
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              opacity: 0.4,
              animation: 'chat-dot-pulse 1.4s ease-in-out infinite',
              animationDelay: `${idx * 0.2}s`,
              '@keyframes chat-dot-pulse': {
                '0%, 80%, 100%': {
                  opacity: 0.4,
                  transform: 'scale(0.8)',
                },
                '40%': {
                  opacity: 1,
                  transform: 'scale(1.2)',
                },
              },
            }}
          />
        ))}
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
        oneVIEW is thinking...
      </Typography>
    </Stack>
  );

  return (
    <Stack
      data-chat-message={me ? 'user' : 'assistant'}
      justifyContent={me ? 'flex-end' : 'flex-start'}
      sx={{
        mb: 2,
        width: '100%'
      }}
    >
      <Stack
        sx={{
          alignItems: me ? 'flex-end' : 'flex-start',
        }}
      >
        {!!message.attachments?.length && (
          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent={me ? 'flex-end' : 'flex-start'}
            sx={{ gap: 1, mb: 1, maxWidth: '100%' }}
          >
            {message.attachments.map((attachment, index) => (
              <Stack
                key={`${attachment.name}-${index}`}
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={attachment.s3Key ? () => handleDownloadAttachment(attachment) : undefined}
                sx={{
                  py: 0.75,
                  px: 1,
                  maxWidth: 220,
                  borderRadius: 1.5,
                  border: (theme) => `1px solid ${theme.vars.palette.divider}`,
                  bgcolor: 'background.paper',
                  cursor: attachment.s3Key ? 'pointer' : 'default',
                  '&:hover': attachment.s3Key
                    ? { bgcolor: (theme) => theme.vars.palette.action.hover }
                    : undefined,
                }}
              >
                <FileThumbnailAny file={attachment.name} sx={{ width: 32, height: 32 }} />
                <Stack sx={{ minWidth: 0 }}>
                  <Typography variant="caption" noWrap sx={{ fontWeight: 600, display: 'block' }}>
                    {attachment.name}
                  </Typography>
                  {!!attachment.size && (
                    <Typography variant="caption" color="text.secondary">
                      {fData(attachment.size)}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}

        {(!!body || isLoadingMessage || hasImage) && (
        <Stack
          data-chat-bubble={me ? 'true' : undefined}
          sx={{
            py: hasImage ? 0 : me ? 1.5 : 0.5,
            px: hasImage ? 0 : me ? 2.5 : 0,
            minWidth: 48,
            maxWidth: '100%',
            borderRadius: me ? 2 : 0,
            fontSize: '1rem',
            lineHeight: 1.6,
            bgcolor: (theme) =>
              me
                ? theme.vars.palette.background.neutral
                : 'transparent',
            color: 'text.primary',
          }}
        >
          {hasImage ? (
            <Box
              component="img"
              alt="attachment"
              src={body}
              onClick={() => onOpenLightbox(body)}
              sx={{
                width: 480,
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 1.5,
                cursor: 'pointer',
                objectFit: 'cover',
                aspectRatio: '16/11',
                '&:hover': { opacity: 0.92 },
              }}
            />
          ) : isLoadingMessage && !me ? (
            renderThinking
          ) : (
            <Markdown
              raw
              sx={{
                typography: 'body1',
                fontSize: 'inherit',
                overflowWrap: 'break-word',
                lineHeight: 'inherit',
                color: 'inherit',
                '& p': { m: 0 },
                '& p + p': { mt: 1.25 },
                '& ul, & ol': { my: 1, pl: 2.5 },
                '& li + li': { mt: 0.5 },
                '& pre': {
                  my: 1.5,
                  borderRadius: 1.5,
                  bgcolor: (theme) => me ? 'rgba(0,0,0,0.05)' : theme.vars.palette.background.neutral,
                  p: 1.5,
                  overflow: 'auto',
                },
                '& code': {
                  fontSize: '0.86em',
                  fontFamily: 'monospace',
                },
              }}
            >
              {`${body ?? ''}`}
            </Markdown>
          )}
        </Stack>
        )}

        {!me && !hasImage && !isLoadingMessage && (
          <Box data-print-hide sx={{ mt: 0.5 }}>
            <ChatMessageFeedback
              value={message.feedback?.rating ?? null}
              onSubmit={(feedback) => onFeedback?.(message.id, feedback)}
            />
          </Box>
        )}
      </Stack>
    </Stack>
  );
}
